import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Home,
  DollarSign,
  FileText,
  ClipboardCheck,
  Wallet,
  Plus,
  Trash2,
  Save,
  Edit3,
  X,
  Calendar,
  User,
  MapPin,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Package,
} from "lucide-react";
import { realEstateApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";

const statusColors: Record<string, string> = {
  pre_close: "bg-amber-100 text-amber-700",
  funding: "bg-blue-100 text-blue-700",
  closed: "bg-emerald-100 text-emerald-700",
  post_close: "bg-slate-100 text-slate-600",
};

const typeColors: Record<string, string> = {
  purchase: "bg-cyan-100 text-cyan-700",
  sale: "bg-purple-100 text-purple-700",
  refinance: "bg-indigo-100 text-indigo-700",
};

const formatLabel = (s: string) =>
  s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const formatCents = (cents: number) => `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

const TABS = [
  { key: "overview", label: "Overview", icon: Home },
  { key: "deposits", label: "Deposits", icon: DollarSign },
  { key: "soa", label: "SOA", icon: FileText },
  { key: "closing-funds", label: "Closing Funds", icon: Wallet },
  { key: "audit", label: "Audit Package", icon: Package },
];

const statusFlow = ["pre_close", "funding", "closed", "post_close"];

const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [txn, setTxn] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [saving, setSaving] = useState(false);

  // SOA state
  const [soa, setSoa] = useState<any>({ purchasePriceCents: 0, adjustments: [], totalDueCents: 0 });

  // Deposit form
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositForm, setDepositForm] = useState({ amountCents: 0, heldBy: "lawyer" as string, reference: "" });

  // SOA adjustment form
  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState({ label: "", amountCents: 0, direction: "credit" as string });

  // Closing funds
  const [closingFunds, setClosingFunds] = useState<any>({ sources: [], payouts: [], approvals: [] });
  const [showSourceForm, setShowSourceForm] = useState(false);
  const [sourceForm, setSourceForm] = useState({ label: "", amountCents: 0, from: "trust" as string });
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ payeeName: "", amountCents: 0, method: "wire" as string, notes: "" });

  // Approval note
  const [approvalNote, setApprovalNote] = useState("");

  const fetchTxn = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await realEstateApi.getTransaction(id);
      setTxn(res.data);
      setClosingFunds(res.data.closingFundsPlan || { sources: [], payouts: [], approvals: [] });
    } catch (err) {
      console.error("Failed to fetch transaction:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTxn();
  }, [fetchTxn]);

  // Fetch SOA when tab changes
  useEffect(() => {
    if (activeTab === "soa" && id) {
      realEstateApi.getSoa(id).then((res) => {
        setSoa(res.data);
      }).catch(() => {});
    }
  }, [activeTab, id]);

  const handleStatusAdvance = async () => {
    if (!id || !txn) return;
    const currentIdx = statusFlow.indexOf(txn.status);
    if (currentIdx < 0 || currentIdx >= statusFlow.length - 1) return;
    const next = statusFlow[currentIdx + 1];
    setSaving(true);
    try {
      await realEstateApi.changeStatus(id, next);
      await fetchTxn();
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to change status");
    } finally {
      setSaving(false);
    }
  };

  // Deposits
  const handleAddDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      await realEstateApi.addDeposit(id, {
        amountCents: depositForm.amountCents,
        heldBy: depositForm.heldBy,
        reference: depositForm.reference || null,
        receivedAt: new Date().toISOString(),
      });
      await fetchTxn();
      setShowDepositForm(false);
      setDepositForm({ amountCents: 0, heldBy: "lawyer", reference: "" });
    } catch (err) {
      console.error("Failed to add deposit:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveDeposit = async (index: number) => {
    if (!id) return;
    try {
      await realEstateApi.removeDeposit(id, index);
      await fetchTxn();
    } catch (err) {
      console.error("Failed to remove deposit:", err);
    }
  };

  // SOA
  const handleSaveSoa = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res = await realEstateApi.updateSoa(id, {
        purchasePriceCents: soa.purchasePriceCents,
        adjustments: soa.adjustments,
      });
      setSoa(res.data);
    } catch (err) {
      console.error("Failed to save SOA:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAdjustment = (e: React.FormEvent) => {
    e.preventDefault();
    setSoa((prev: any) => ({
      ...prev,
      adjustments: [...prev.adjustments, adjForm],
    }));
    setShowAdjForm(false);
    setAdjForm({ label: "", amountCents: 0, direction: "credit" });
  };

  const handleRemoveAdjustment = (index: number) => {
    setSoa((prev: any) => ({
      ...prev,
      adjustments: prev.adjustments.filter((_: any, i: number) => i !== index),
    }));
  };

  // Closing funds
  const handleSaveClosingFunds = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res = await realEstateApi.updateClosingFunds(id, {
        sources: closingFunds.sources,
        payouts: closingFunds.payouts,
      });
      setClosingFunds(res.data);
      await fetchTxn();
    } catch (err) {
      console.error("Failed to save closing funds:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res = await realEstateApi.approveClosingFunds(id, approvalNote || undefined);
      setClosingFunds(res.data);
      setApprovalNote("");
      await fetchTxn();
    } catch (err) {
      console.error("Failed to approve:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!id || !approvalNote) return;
    setSaving(true);
    try {
      const res = await realEstateApi.rejectClosingFunds(id, approvalNote);
      setClosingFunds(res.data);
      setApprovalNote("");
      await fetchTxn();
    } catch (err) {
      console.error("Failed to reject:", err);
    } finally {
      setSaving(false);
    }
  };

  // Audit Package
  const handleGenerateAudit = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await realEstateApi.generateAuditPackage(id);
      await fetchTxn();
    } catch (err) {
      console.error("Failed to generate audit:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!txn) {
    return (
      <div className="text-center py-20 text-slate-500">
        <p className="text-lg font-medium">Transaction not found</p>
        <button onClick={() => navigate("/real-estate")} className="mt-4 text-brand-accent hover:underline">
          Back to transactions
        </button>
      </div>
    );
  }

  const getAddr = (p: any) => {
    if (!p || typeof p !== "object") return "Unknown";
    const a = p.address || {};
    return [a.line1, a.city, a.region, a.postal].filter(Boolean).join(", ") || "No address";
  };

  const getClientName = (c: any) => (typeof c === "object" ? c?.displayName : "Unknown");
  const getContactName = (c: any) => (typeof c === "object" ? (c?.displayName || c?.name) : null);

  const totalDeposits = (txn.deposits || []).reduce((sum: number, d: any) => sum + (d.amountCents || 0), 0);
  const totalSources = closingFunds.sources?.reduce((s: number, src: any) => s + (src.amountCents || 0), 0) || 0;
  const totalPayouts = closingFunds.payouts?.reduce((s: number, p: any) => s + (p.amountCents || 0), 0) || 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/real-estate")}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-800 truncate">{getAddr(txn.propertyId)}</h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeColors[txn.type] || "bg-slate-100 text-slate-600"}`}>
              {formatLabel(txn.type)}
            </span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[txn.status] || "bg-slate-100 text-slate-600"}`}>
              {formatLabel(txn.status)}
            </span>
            {txn.status !== "post_close" && (
              <button
                onClick={handleStatusAdvance}
                disabled={saving}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: "var(--color-accent)" }}
              >
                Advance <ChevronRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-6 -mb-px overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? "border-brand-accent text-brand"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.15 }}
        >
          {/* ─── Overview Tab ─── */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Property Info */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <MapPin className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                  Property
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Address</span>
                    <span className="font-medium text-slate-700 text-right">{getAddr(txn.propertyId)}</span>
                  </div>
                  {txn.propertyId?.legalDescription && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Legal Description</span>
                      <span className="font-medium text-slate-700">{txn.propertyId.legalDescription}</span>
                    </div>
                  )}
                  {txn.propertyId?.pin && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">PIN</span>
                      <span className="font-medium text-slate-700 font-mono">{txn.propertyId.pin}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Parties */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                  Parties
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Client</span>
                    <span className="font-medium text-slate-700">{getClientName(txn.clientId)}</span>
                  </div>
                  {getContactName(txn.parties?.buyerClientId) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Buyer</span>
                      <span className="font-medium text-slate-700">{getContactName(txn.parties.buyerClientId)}</span>
                    </div>
                  )}
                  {getContactName(txn.parties?.sellerContactId) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Seller</span>
                      <span className="font-medium text-slate-700">{getContactName(txn.parties.sellerContactId)}</span>
                    </div>
                  )}
                  {getContactName(txn.parties?.lenderContactId) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Lender</span>
                      <span className="font-medium text-slate-700">{getContactName(txn.parties.lenderContactId)}</span>
                    </div>
                  )}
                  {getContactName(txn.parties?.realtorContactId) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">Realtor</span>
                      <span className="font-medium text-slate-700">{getContactName(txn.parties.realtorContactId)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Key Dates */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Calendar className="h-4 w-4" style={{ color: "var(--color-accent)" }} />
                  Key Dates
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Closing Date</span>
                    <span className="font-medium text-slate-700">
                      {txn.keyDates?.closingDate
                        ? new Date(txn.keyDates.closingDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                        : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Requisition Date</span>
                    <span className="font-medium text-slate-700">
                      {txn.keyDates?.requisitionDate
                        ? new Date(txn.keyDates.requisitionDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                        : "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-4">Transaction Status</h3>
                <div className="flex items-center gap-2">
                  {statusFlow.map((s, i) => (
                    <React.Fragment key={s}>
                      <div className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium ${
                        s === txn.status ? statusColors[s] : statusFlow.indexOf(txn.status) > i ? "bg-brand-light text-brand-accent" : "bg-slate-50 text-slate-400"
                      }`}>
                        {statusFlow.indexOf(txn.status) > i && <CheckCircle2 className="h-3.5 w-3.5" />}
                        {formatLabel(s)}
                      </div>
                      {i < statusFlow.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Deposits Tab ─── */}
          {activeTab === "deposits" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-slate-800">Deposits ({txn.deposits?.length || 0})</h3>
                  <p className="text-sm text-slate-500">Total: {formatCents(totalDeposits)}</p>
                </div>
                <button
                  onClick={() => setShowDepositForm(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  <Plus className="h-4 w-4" />
                  Add Deposit
                </button>
              </div>

              {txn.deposits?.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-10 text-center text-slate-500">
                  <DollarSign className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                  <p>No deposits yet</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">#</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Held By</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Reference</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Received</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {txn.deposits?.map((d: any, i: number) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="px-6 py-3 text-sm text-slate-500">{i + 1}</td>
                          <td className="px-6 py-3 text-sm font-medium text-slate-800">{formatCents(d.amountCents)}</td>
                          <td className="px-6 py-3 text-sm text-slate-600 capitalize">{d.heldBy}</td>
                          <td className="px-6 py-3 text-sm text-slate-600 font-mono">{d.reference || "-"}</td>
                          <td className="px-6 py-3 text-sm text-slate-600">
                            {d.receivedAt ? new Date(d.receivedAt).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-6 py-3">
                            <button
                              onClick={() => handleRemoveDeposit(i)}
                              className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-3 text-sm font-bold text-slate-800" colSpan={1}>Total</td>
                        <td className="px-6 py-3 text-sm font-bold text-slate-800">{formatCents(totalDeposits)}</td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Add Deposit Form */}
              <AnimatePresence>
                {showDepositForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
                  >
                    <form onSubmit={handleAddDeposit} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Amount ($) *</label>
                        <input
                          type="number"
                          required
                          step="0.01"
                          min="0"
                          value={depositForm.amountCents / 100 || ""}
                          onChange={(e) => setDepositForm({ ...depositForm, amountCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Held By</label>
                        <SearchableSelect
                          options={[
                            { value: "broker", label: "Broker" },
                            { value: "lawyer", label: "Lawyer" },
                            { value: "other", label: "Other" },
                          ]}
                          value={depositForm.heldBy}
                          onChange={(val) => setDepositForm({ ...depositForm, heldBy: val || "lawyer" })}
                          placeholder="Select holder"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
                        <input
                          type="text"
                          value={depositForm.reference}
                          onChange={(e) => setDepositForm({ ...depositForm, reference: e.target.value })}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                        />
                      </div>
                      <div className="sm:col-span-3 flex justify-end gap-3">
                        <button type="button" onClick={() => setShowDepositForm(false)} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancel</button>
                        <button type="submit" disabled={saving} className="px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50" style={{ backgroundColor: "var(--color-primary)" }}>
                          {saving ? "Adding..." : "Add Deposit"}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* ─── SOA Tab ─── */}
          {activeTab === "soa" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Statement of Adjustments</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveSoa}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-primary)" }}
                  >
                    <Save className="h-4 w-4" />
                    {saving ? "Saving..." : "Save SOA"}
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
                {/* Purchase Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Purchase Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={soa.purchasePriceCents / 100 || ""}
                    onChange={(e) => setSoa({ ...soa, purchasePriceCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                    className="w-full max-w-xs rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>

                {/* Adjustments */}
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-semibold text-slate-700">Adjustments</h4>
                    <button
                      onClick={() => setShowAdjForm(true)}
                      className="text-xs font-medium text-brand-accent hover:text-brand inline-flex items-center gap-1"
                    >
                      <Plus className="h-3 w-3" /> Add Adjustment
                    </button>
                  </div>

                  {soa.adjustments?.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 text-xs font-semibold text-slate-600 uppercase">Description</th>
                          <th className="text-right py-2 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                          <th className="text-center py-2 text-xs font-semibold text-slate-600 uppercase">Direction</th>
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {soa.adjustments.map((adj: any, i: number) => (
                          <tr key={i} className="border-b border-slate-100">
                            <td className="py-2 text-sm text-slate-800">{adj.label}</td>
                            <td className="py-2 text-sm text-right font-medium text-slate-800">{formatCents(adj.amountCents)}</td>
                            <td className="py-2 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                adj.direction === "credit" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                              }`}>
                                {adj.direction === "credit" ? "Credit" : "Debit"}
                              </span>
                            </td>
                            <td className="py-2 text-right">
                              <button onClick={() => handleRemoveAdjustment(i)} className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-slate-500">No adjustments added</p>
                  )}
                </div>

                {/* Add Adjustment Form */}
                <AnimatePresence>
                  {showAdjForm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <form onSubmit={handleAddAdjustment} className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-xl">
                        <input
                          type="text"
                          required
                          placeholder="Description"
                          value={adjForm.label}
                          onChange={(e) => setAdjForm({ ...adjForm, label: e.target.value })}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                        <input
                          type="number"
                          required
                          step="0.01"
                          min="0"
                          placeholder="Amount ($)"
                          value={adjForm.amountCents / 100 || ""}
                          onChange={(e) => setAdjForm({ ...adjForm, amountCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                        />
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <SearchableSelect
                              options={[
                                { value: "credit", label: "Credit" },
                                { value: "debit", label: "Debit" },
                              ]}
                              value={adjForm.direction}
                              onChange={(val) => setAdjForm({ ...adjForm, direction: val || "credit" })}
                              placeholder="Direction"
                            />
                          </div>
                          <button type="submit" className="px-3 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: "var(--color-primary)" }}>Add</button>
                          <button type="button" onClick={() => setShowAdjForm(false)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Calculated Total */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-slate-800">Total Due on Closing</span>
                    <span className="text-lg font-bold text-slate-800">{formatCents(soa.totalDueCents || 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── Closing Funds Tab ─── */}
          {activeTab === "closing-funds" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Closing Funds Plan</h3>
                <button
                  onClick={handleSaveClosingFunds}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  <Save className="h-4 w-4" />
                  {saving ? "Saving..." : "Save Plan"}
                </button>
              </div>

              {/* Balance indicator */}
              <div className={`bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between ${
                totalSources === totalPayouts && totalSources > 0 ? "border-emerald-200" : totalSources > 0 || totalPayouts > 0 ? "border-amber-200" : "border-slate-200"
              }`}>
                <div className="text-sm">
                  <span className="text-slate-500">Sources: </span>
                  <span className="font-bold text-slate-800">{formatCents(totalSources)}</span>
                  <span className="text-slate-400 mx-2">|</span>
                  <span className="text-slate-500">Payouts: </span>
                  <span className="font-bold text-slate-800">{formatCents(totalPayouts)}</span>
                </div>
                {totalSources === totalPayouts && totalSources > 0 ? (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Balanced</span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    Difference: {formatCents(Math.abs(totalSources - totalPayouts))}
                  </span>
                )}
              </div>

              {/* Sources */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-slate-700">Sources</h4>
                  <button
                    onClick={() => setShowSourceForm(true)}
                    className="text-xs font-medium text-brand-accent hover:text-brand inline-flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add Source
                  </button>
                </div>
                {closingFunds.sources?.length > 0 ? (
                  <div className="space-y-2">
                    {closingFunds.sources.map((src: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium text-slate-800">{src.label}</span>
                          <span className="text-xs text-slate-500 ml-2 capitalize">({src.from})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{formatCents(src.amountCents)}</span>
                          <button
                            onClick={() => setClosingFunds((prev: any) => ({ ...prev, sources: prev.sources.filter((_: any, idx: number) => idx !== i) }))}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No sources added</p>
                )}

                <AnimatePresence>
                  {showSourceForm && (
                    <motion.form
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={(e) => {
                        e.preventDefault();
                        setClosingFunds((prev: any) => ({ ...prev, sources: [...prev.sources, sourceForm] }));
                        setShowSourceForm(false);
                        setSourceForm({ label: "", amountCents: 0, from: "trust" });
                      }}
                      className="grid grid-cols-3 gap-3 mt-3 p-3 bg-slate-50 rounded-xl"
                    >
                      <input type="text" required placeholder="Label" value={sourceForm.label}
                        onChange={(e) => setSourceForm({ ...sourceForm, label: e.target.value })}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <input type="number" required step="0.01" min="0" placeholder="Amount ($)" value={sourceForm.amountCents / 100 || ""}
                        onChange={(e) => setSourceForm({ ...sourceForm, amountCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <SearchableSelect
                            options={[
                              { value: "trust", label: "Trust" },
                              { value: "wire", label: "Wire" },
                              { value: "bankdraft", label: "Bank Draft" },
                              { value: "mortgage_advance", label: "Mortgage Advance" },
                              { value: "other", label: "Other" },
                            ]}
                            value={sourceForm.from}
                            onChange={(val) => setSourceForm({ ...sourceForm, from: val || "trust" })}
                            placeholder="Source type"
                          />
                        </div>
                        <button type="submit" className="px-3 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: "var(--color-primary)" }}>Add</button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>

              {/* Payouts */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-semibold text-slate-700">Payouts</h4>
                  <button
                    onClick={() => setShowPayoutForm(true)}
                    className="text-xs font-medium text-brand-accent hover:text-brand inline-flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Add Payout
                  </button>
                </div>
                {closingFunds.payouts?.length > 0 ? (
                  <div className="space-y-2">
                    {closingFunds.payouts.map((p: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium text-slate-800">{p.payeeName}</span>
                          <span className="text-xs text-slate-500 ml-2 capitalize">({p.method})</span>
                          {p.notes && <span className="text-xs text-slate-400 ml-2">- {p.notes}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{formatCents(p.amountCents)}</span>
                          <button
                            onClick={() => setClosingFunds((prev: any) => ({ ...prev, payouts: prev.payouts.filter((_: any, idx: number) => idx !== i) }))}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No payouts added</p>
                )}

                <AnimatePresence>
                  {showPayoutForm && (
                    <motion.form
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={(e) => {
                        e.preventDefault();
                        setClosingFunds((prev: any) => ({ ...prev, payouts: [...prev.payouts, payoutForm] }));
                        setShowPayoutForm(false);
                        setPayoutForm({ payeeName: "", amountCents: 0, method: "wire", notes: "" });
                      }}
                      className="grid grid-cols-2 gap-3 mt-3 p-3 bg-slate-50 rounded-xl"
                    >
                      <input type="text" required placeholder="Payee Name" value={payoutForm.payeeName}
                        onChange={(e) => setPayoutForm({ ...payoutForm, payeeName: e.target.value })}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <input type="number" required step="0.01" min="0" placeholder="Amount ($)" value={payoutForm.amountCents / 100 || ""}
                        onChange={(e) => setPayoutForm({ ...payoutForm, amountCents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
                      <SearchableSelect
                        options={[
                          { value: "wire", label: "Wire" },
                          { value: "cheque", label: "Cheque" },
                          { value: "eft", label: "EFT" },
                        ]}
                        value={payoutForm.method}
                        onChange={(val) => setPayoutForm({ ...payoutForm, method: val || "wire" })}
                        placeholder="Payment method"
                      />
                      <div className="flex gap-2">
                        <input type="text" placeholder="Notes" value={payoutForm.notes}
                          onChange={(e) => setPayoutForm({ ...payoutForm, notes: e.target.value })}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm flex-1" />
                        <button type="submit" className="px-3 py-2 rounded-lg text-white text-sm font-medium" style={{ backgroundColor: "var(--color-primary)" }}>Add</button>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>

              {/* Approvals */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h4 className="font-semibold text-slate-700 mb-4">Approval Workflow</h4>

                {closingFunds.approvals?.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {closingFunds.approvals.map((a: any, i: number) => (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${
                        a.status === "approved" ? "bg-emerald-50" : a.status === "rejected" ? "bg-red-50" : "bg-slate-50"
                      }`}>
                        {a.status === "approved" ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600" />
                        )}
                        <div className="flex-1">
                          <span className="text-sm font-medium capitalize">{a.status}</span>
                          {a.note && <span className="text-sm text-slate-600 ml-2">- {a.note}</span>}
                        </div>
                        <span className="text-xs text-slate-500">
                          {a.at ? new Date(a.at).toLocaleString() : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Approval note..."
                    value={approvalNote}
                    onChange={(e) => setApprovalNote(e.target.value)}
                    className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-600"
                  />
                  <button
                    onClick={handleApprove}
                    disabled={saving}
                    className="px-4 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                    style={{ backgroundColor: "var(--color-accent)" }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={saving || !approvalNote}
                    className="px-4 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 bg-red-600"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ─── Audit Package Tab ─── */}
          {activeTab === "audit" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Audit Package</h3>
                <button
                  onClick={handleGenerateAudit}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
                  style={{ backgroundColor: "var(--color-primary)" }}
                >
                  <Package className="h-4 w-4" />
                  {saving ? "Generating..." : "Generate Package"}
                </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                {txn.auditPackage?.generatedAt ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                      <div>
                        <p className="font-medium text-slate-800">Audit package generated</p>
                        <p className="text-sm text-slate-500">
                          Generated on{" "}
                          {new Date(txn.auditPackage.generatedAt).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-slate-700">Document Checklist</h4>
                      {[
                        "Statement of Adjustments",
                        "Closing Funds Summary",
                        "Trust Ledger Report",
                        "Deposit Receipts",
                        "Title Search",
                        "Undertakings",
                      ].map((doc, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                          <ClipboardCheck className="h-4 w-4 text-emerald-500" />
                          <span className="text-sm text-slate-700">{doc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10 text-slate-500">
                    <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No audit package generated yet</p>
                    <p className="text-sm mt-1">Click "Generate Package" to create the audit bundle</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TransactionDetail;
